import React from "react";
import { motion } from "framer-motion";
import {
  Award,
  TrendingUp,
  Shield,
  Star,
  CheckCircle2,
  Lock,
  Unlock,
  Users,
  DollarSign,
  Target,
  Info,
  ArrowRight,
} from "lucide-react";

const PublisherLevelAssignment = ({ promoter, overallScore = 0 }) => {
  const levels = [
    {
      level: 1,
      name: "Basic Publisher",
      description: "Entry level for all new publishers",
      requirements: "Default starting level",
      access: [
        "Access to general campaigns",
        "Standard commission rates",
        "Basic campaign analytics",
        "Email support",
      ],
      restrictions: [
        "No premium campaign access",
        "Limited to standard commission structure",
        "Monthly performance review required",
      ],
      benefits: [
        "Learning & monitoring phase",
        "Platform onboarding support",
        "Access to training resources",
      ],
      commissionRate: "Standard",
      minScore: 0,
      maxScore: 60,
      color: "purple",
      icon: Shield,
      rationale: "Prevents abuse and protects brands while new publishers learn the platform and establish performance history.",
    },
    {
      level: 2,
      name: "Established Publisher",
      description: "For publishers with proven performance",
      requirements: "Score ≥ 60, 3+ months active, positive compliance record",
      access: [
        "Access to premium campaigns",
        "Enhanced commission rates",
        "Advanced campaign analytics",
        "Priority support",
        "Early access to new campaigns",
      ],
      restrictions: [
        "Must maintain minimum performance standards",
        "Quarterly review required",
      ],
      benefits: [
        "Higher earning potential",
        "Dedicated account manager",
        "Campaign recommendations",
      ],
      commissionRate: "Enhanced",
      minScore: 60,
      maxScore: 75,
      color: "blue",
      icon: TrendingUp,
      rationale: "Rewards consistent performance and provides growth opportunities for reliable publishers.",
    },
    {
      level: 3,
      name: "Premium Publisher",
      description: "Top performers with exceptional track record",
      requirements: "Score ≥ 75, 6+ months active, excellent compliance, high conversion rates",
      access: [
        "Exclusive premium campaigns",
        "Maximum commission rates",
        "Full analytics dashboard",
        "24/7 priority support",
        "First access to exclusive deals",
        "Custom campaign opportunities",
      ],
      restrictions: [
        "Must maintain excellent performance",
        "Semi-annual review",
      ],
      benefits: [
        "Highest earning potential",
        "Personal account manager",
        "Strategic campaign planning",
        "Performance bonuses",
      ],
      commissionRate: "Maximum",
      minScore: 75,
      maxScore: 100,
      color: "gold",
      icon: Star,
      rationale: "Recognizes top performers and provides maximum incentives for exceptional publishers who drive significant value.",
    },
  ];

  const getCurrentLevel = () => {
    // All publishers start at Level 1
    return levels[0];
  };

  const currentLevel = getCurrentLevel();
  const nextLevel = levels.find((l) => l.level === currentLevel.level + 1);

  const getColorClasses = (color) => {
    const colors = {
      purple: {
        bg: "bg-purple-50",
        border: "border-purple-200",
        text: "text-purple-900",
        icon: "text-purple-600",
        iconBg: "bg-purple-100",
        badge: "bg-purple-100 text-purple-700 border-purple-300",
      },
      blue: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-900",
        icon: "text-blue-600",
        iconBg: "bg-blue-100",
        badge: "bg-blue-100 text-blue-700 border-blue-300",
      },
      gold: {
        bg: "bg-amber-50",
        border: "border-amber-200",
        text: "text-amber-900",
        icon: "text-amber-600",
        iconBg: "bg-amber-100",
        badge: "bg-amber-100 text-amber-700 border-amber-300",
      },
    };
    return colors[color] || colors.purple;
  };

  const currentColors = getColorClasses(currentLevel.color);
  const progressToNext = nextLevel
    ? ((overallScore - currentLevel.minScore) / (nextLevel.minScore - currentLevel.minScore)) * 100
    : 100;

  return (
    <div className="space-y-6">
      {/* Current Level Assignment */}
      <div className={`p-6 ${currentColors.bg} rounded-xl border-2 ${currentColors.border} shadow-lg`}>
        <div className="flex gap-4 items-start mb-6">
          <div className={`p-4 ${currentColors.iconBg} rounded-xl`}>
            <currentLevel.icon className={`w-8 h-8 ${currentColors.icon}`} />
          </div>
          <div className="flex-1">
            <div className="flex gap-3 items-center mb-2">
              <h3 className="text-2xl font-bold text-gray-900">
                Level {currentLevel.level} – {currentLevel.name}
              </h3>
              <span className={`px-3 py-1 text-sm font-bold rounded-full border ${currentColors.badge}`}>
                CURRENT LEVEL
              </span>
            </div>
            <p className="mb-4 text-gray-700">{currentLevel.description}</p>
            <p className="text-sm italic text-gray-600">
              <strong>Rationale:</strong> {currentLevel.rationale}
            </p>
          </div>
        </div>

        {/* Level Details Grid */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 mb-6">
          {/* Access */}
          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <div className="flex gap-2 items-center mb-3">
              <Unlock className="w-5 h-5 text-green-600" />
              <h4 className="font-semibold text-gray-900">Access & Benefits</h4>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              {currentLevel.access.map((item, idx) => (
                <li key={idx} className="flex gap-2 items-start">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {currentLevel.benefits.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-600 mb-2">Additional Benefits:</p>
                <ul className="space-y-1 text-xs text-gray-600">
                  {currentLevel.benefits.map((item, idx) => (
                    <li key={idx} className="flex gap-1 items-start">
                      <Star className="w-3 h-3 mt-0.5 text-amber-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Restrictions */}
          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <div className="flex gap-2 items-center mb-3">
              <Lock className="w-5 h-5 text-amber-600" />
              <h4 className="font-semibold text-gray-900">Restrictions</h4>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              {currentLevel.restrictions.map((item, idx) => (
                <li key={idx} className="flex gap-2 items-start">
                  <Info className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Commission */}
          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <div className="flex gap-2 items-center mb-3">
              <DollarSign className="w-5 h-5 text-green-600" />
              <h4 className="font-semibold text-gray-900">Commission Rate</h4>
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-2xl font-bold text-green-700 mb-1">
                {currentLevel.commissionRate}
              </p>
              <p className="text-xs text-gray-600">
                Based on campaign type and performance
              </p>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                <strong>Note:</strong> Commission rates may vary by campaign and are subject to terms and conditions.
              </p>
            </div>
          </div>
        </div>

        {/* Progress to Next Level */}
        {nextLevel && (
          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <div className="flex gap-3 items-center justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900">Progress to Level {nextLevel.level}</h4>
                <p className="text-sm text-gray-600">
                  Current Score: {overallScore.toFixed(1)} / {nextLevel.minScore} required
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-600">
                  {Math.max(0, Math.min(100, progressToNext)).toFixed(1)}%
                </p>
              </div>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.max(0, Math.min(100, progressToNext))}%` }}
                transition={{ duration: 1 }}
                className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"
              />
            </div>
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Next Level Requirements:</strong> {nextLevel.requirements}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* All Levels Overview */}
      <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
        <h4 className="mb-4 text-lg font-bold text-gray-900">Publisher Level System Overview</h4>
        <div className="space-y-4">
          {levels.map((level, index) => {
            const levelColors = getColorClasses(level.color);
            const Icon = level.icon;
            const isCurrent = level.level === currentLevel.level;

            return (
              <motion.div
                key={level.level}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`p-4 rounded-lg border-2 ${
                  isCurrent
                    ? `${levelColors.border} ${levelColors.bg} ring-2 ring-offset-2 ${
                        level.color === "purple"
                          ? "ring-purple-400"
                          : level.color === "blue"
                          ? "ring-blue-400"
                          : "ring-amber-400"
                      }`
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex gap-4 items-start">
                  <div className={`p-3 ${levelColors.iconBg} rounded-lg`}>
                    <Icon className={`w-6 h-6 ${levelColors.icon}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex gap-3 items-center mb-2">
                      <h5 className="text-lg font-bold text-gray-900">
                        Level {level.level} – {level.name}
                      </h5>
                      {isCurrent && (
                        <span className="px-2 py-1 text-xs font-bold text-white bg-green-600 rounded">
                          CURRENT
                        </span>
                      )}
                    </div>
                    <p className="mb-2 text-sm text-gray-600">{level.description}</p>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 mt-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-1">Commission:</p>
                        <p className="text-sm font-medium text-gray-900">{level.commissionRate}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-1">Score Range:</p>
                        <p className="text-sm font-medium text-gray-900">
                          {level.minScore} - {level.maxScore}
                        </p>
                      </div>
                    </div>
                  </div>
                  {index < levels.length - 1 && (
                    <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-2" />
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Level Upgrade Path */}
      <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
        <div className="flex gap-3 items-start">
          <Target className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-1" />
          <div>
            <h4 className="mb-2 font-bold text-gray-900">Level Upgrade Path</h4>
            <p className="mb-3 text-sm text-gray-700">
              Publishers can advance through levels based on consistent performance and scoring:
            </p>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex gap-2 items-center">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Monthly performance reviews evaluate scoring metrics</span>
              </div>
              <div className="flex gap-2 items-center">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Consistent positive performance leads to level upgrades</span>
              </div>
              <div className="flex gap-2 items-center">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Violations or poor performance may result in restrictions or level downgrades</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublisherLevelAssignment;
